import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Check } from "lucide-react";

export interface SelectOption {
  value: string;
  label: string;
}

interface Props {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  /** Show a leading option that clears the selection (e.g. "Wszyscy"). */
  clearLabel?: string;
  className?: string;
}

/** Single-select dropdown with a search box and a scrollable option list. */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "— wybierz —",
  loading = false,
  disabled = false,
  clearLabel,
  className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  function pick(v: string) {
    onChange(v);
    setOpen(false);
  }

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-primary)] disabled:opacity-60"
      >
        <span className={selectedLabel ? "truncate" : "truncate text-[var(--color-text-muted)]"}>
          {loading ? "Ładowanie…" : selectedLabel ?? placeholder}
        </span>
        <ChevronDown size={15} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-[var(--color-bg-surface)] border border-[var(--color-border)] shadow-lg">
          <div className="relative border-b border-[var(--color-border)]">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Szukaj…"
              autoFocus
              className="w-full pl-8 pr-3 py-2 text-sm bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {clearLabel !== undefined && (
              <button
                type="button"
                onClick={() => pick("")}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]"
              >
                {clearLabel}
                {value === "" && <Check size={14} />}
              </button>
            )}
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-[var(--color-text-muted)]">Brak wyników.</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => pick(o.value)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]"
                >
                  <span className="truncate">{o.label}</span>
                  {value === o.value && <Check size={14} className="shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
